/* Editor — Export/Import Novels (2026-10).
 *
 * Preview and production never share a database (or a filesystem),
 * so AI-generated novels written on one never show up on the other.
 * This tool bridges that gap manually: "Export" bundles every genuine
 * Loom novel (projects + chapters + pseudonyms + cover art) into a
 * downloadable .zip on whichever environment you're on; "Import"
 * uploads that same file on the other environment to recreate
 * everything there. Safe to re-run — already-imported rows are
 * skipped by id, nothing is ever duplicated.
 */
import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, Download, Upload, CheckCircle2 } from 'lucide-react';

const API  = (process.env.REACT_APP_BACKEND_URL || '') + '/api';
const http = axios.create({ baseURL: API, withCredentials: true });

export default function EditorExportImportPanel() {
  const [exporting, setExporting] = useState(false);
  const [importing, setImporting] = useState(false);
  const [result,    setResult]    = useState(null);

  const exportBundle = async () => {
    setExporting(true);
    try {
      const r = await http.get('/editor/manuscripts/export-bundle', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement('a');
      const today = new Date().toISOString().slice(0, 10).replace(/-/g, '');
      a.href = url;
      a.download = `loom_novels_export_${today}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Export downloaded — upload this .zip on the other environment to import.');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Export failed.');
    } finally { setExporting(false); }
  };

  const importBundle = async (file) => {
    if (!file) return;
    setImporting(true);
    setResult(null);
    try {
      const form = new FormData();
      form.append('file', file);
      const r = await http.post('/editor/manuscripts/import-bundle', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setResult(r.data);
      toast.success(`Imported ${r.data.projects_imported} novel(s), skipped ${r.data.projects_skipped} already present.`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Import failed.');
    } finally { setImporting(false); }
  };

  return (
    <section data-testid="editor-export-import-panel" className="space-y-4">
      <div>
        <div className="text-[13px] font-semibold">Export / Import Novels</div>
        <div className="text-[10.5px] text-muted-foreground">
          Move every genuine Loom AI novel (text, pseudonyms, cover art) between environments
          that don't share a database — e.g. preview to production.
        </div>
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        <div className="rounded-md border border-border p-3">
          <div className="text-[12px] font-semibold">1. Export from this environment</div>
          <p className="mt-1 text-[11px] text-muted-foreground">
            Downloads a single .zip with every AI-written novel, its chapters, author
            pseudonyms, and cover art currently on this environment.
          </p>
          <button
            type="button"
            onClick={exportBundle}
            disabled={exporting}
            data-testid="editor-export-bundle-btn"
            className="mt-2 inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-[12px] font-semibold text-primary-foreground hover:opacity-90 disabled:opacity-60"
          >
            {exporting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />}
            Export all novels
          </button>
        </div>

        <div className="rounded-md border border-border p-3">
          <div className="text-[12px] font-semibold">2. Import on the other environment</div>
          <p className="mt-1 text-[11px] text-muted-foreground">
            Upload the .zip you just downloaded, on whichever environment should receive
            these novels. Safe to re-run — existing novels are skipped, never duplicated.
          </p>
          <label
            className="mt-2 inline-flex cursor-pointer items-center gap-1.5 rounded-md border border-border px-3 py-1.5 text-[12px] font-semibold hover:bg-secondary disabled:opacity-60"
            data-testid="editor-import-bundle-label"
          >
            {importing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}
            {importing ? 'Importing…' : 'Choose .zip to import'}
            <input
              type="file"
              accept=".zip"
              disabled={importing}
              onChange={(e) => importBundle(e.target.files?.[0])}
              data-testid="editor-import-bundle-input"
              className="hidden"
            />
          </label>
        </div>
      </div>

      {result && (
        <div className="rounded-md border border-emerald-500/30 bg-emerald-500/5 p-3 text-[11.5px]" data-testid="editor-import-result">
          <div className="mb-1.5 inline-flex items-center gap-1.5 font-semibold text-emerald-600">
            <CheckCircle2 className="h-3.5 w-3.5" /> Import complete
          </div>
          <div className="grid grid-cols-2 gap-1 sm:grid-cols-3">
            <span>Novels: {result.projects_imported} new, {result.projects_skipped} already present</span>
            <span>Chapters: {result.chapters_imported} new, {result.chapters_skipped} already present</span>
            <span>Manuscripts: {result.manuscripts_imported} new, {result.manuscripts_skipped} already present</span>
            <span>Pseudonyms: {result.pseudonyms_imported} new, {result.pseudonyms_skipped} already present</span>
            <span>Cover images: {result.images_saved} saved, {result.images_skipped} already present</span>
          </div>
          {result.errors?.length > 0 && (
            <div className="mt-1.5 text-destructive" data-testid="editor-import-errors">
              {result.errors.length} warning(s): {result.errors.join('; ')}
            </div>
          )}
        </div>
      )}
    </section>
  );
}
